import {
  Suspense,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

/**
 * P0-I Phase 1S-1: SelfHealingSuspense
 *
 * A drop-in replacement for <Suspense> that adds a watchdog timer and
 * key-bump remount escape hatch.  Addresses the "Suspense retry
 * starvation" failure mode observed on resource-constrained Firefox /
 * Linux peers (see `docs/connection-desync-audit.md`), where the inner
 * Suspense promise resolves promptly (sub-second) but React's scheduler
 * never picks the boundary's retry back up to commit, leaving the UI
 * stuck on the fallback skeleton for minutes.
 *
 * How it works:
 *  1. On each key-instance, this component schedules a `setTimeout` for
 *     `watchdogMs`.
 *  2. A <CommitSentinel/> placed as the first child of the inner
 *     <Suspense> writes its instance id into a ref when it mounts — the
 *     canonical signal that the boundary has successfully committed the
 *     resolved children.
 *  3. When the watchdog fires it compares the last committed instance id
 *     to the current one.  If they don't match, the boundary never
 *     committed.  We bump the outer `key`, which unmounts the stuck
 *     Suspense and mounts a fresh one with new promises.  Often this is
 *     enough to shake React's scheduler out of its quiescent state.
 *  4. After `maxRemounts` attempts we stop the watchdog and leave the
 *     current Suspense to do its best.  If an `errorFallback` was
 *     provided, it replaces the skeleton so the user at least sees that
 *     something went wrong instead of a perpetual loader.
 *
 * Because all recovery is driven from `setTimeout` and `setState`, the
 * cost when things are working normally is exactly one timer per mount
 * (cleared immediately on commit).
 *
 * NOTE: This wrapper does NOT replace the memory-hygiene fixes in
 *       Phase 1S-L1/L2/L3 — those reduce GC pressure that contributes
 *       to scheduler quiescence.  The wrapper is the last line of
 *       defense after the upstream fixes do their work.
 */
interface SelfHealingSuspenseProps {
  /** Content that may suspend. */
  children: ReactNode;
  /** Shown while suspended (same semantics as React.Suspense). */
  fallback: ReactNode;
  /**
   * Stable, human-readable identifier used in the watchdog console
   * warning.  Pick something specific per boundary (e.g., "chat-panel",
   * "nav-rail", "main-content") so log forensics is easy.
   */
  name: string;
  /**
   * How long to wait for the boundary to commit before bumping the
   * key and remounting.  Defaults to 5 s.  Tune shorter for fine-grained
   * boundaries (per-message ~3 s) and longer for heavy panels where a
   * legitimate initial paint may take time.
   */
  watchdogMs?: number;
  /**
   * Maximum number of key bumps before giving up on watchdog-driven
   * recovery.  Defaults to 2 (so three total commit attempts
   * counting the initial mount).
   */
  maxRemounts?: number;
  /**
   * Optional fallback rendered once `maxRemounts` is exhausted.  Useful
   * for surfacing a manual retry button or error state.  If omitted the
   * regular `fallback` is reused.
   */
  errorFallback?: ReactNode;
  /**
   * P0-I Phase 1S-B: when true, the watchdog continues firing past
   * `maxRemounts` with exponential backoff up to a 60 s ceiling
   * (10 s, 20 s, 40 s, 60 s, 60 s, …).  Use for panel-level boundaries
   * where a permanently stuck UI is unacceptable but where we still want
   * the early `maxRemounts` attempts to be fast.  Off by default so
   * narrow boundaries (per-message, nav-rail) don't accidentally start
   * spawning long-tail timers.
   *
   * The 4/26 K Linux FF run motivated this: chat-list watchdog fired
   * the allowed 2 times, then the wrapper gave up; React's natural
   * Suspense retry never ran (scheduler quiescent), and the panel
   * remained on the skeleton fallback for 2.5+ minutes.  With
   * `unboundedRetry`, the wrapper will keep nudging the boundary back
   * onto the scheduler (one nudge per backoff interval) until it
   * finally commits.
   */
  unboundedRetry?: boolean;
}

export function SelfHealingSuspense({
  children,
  fallback,
  name,
  watchdogMs = 5000,
  maxRemounts = 2,
  errorFallback,
  unboundedRetry = false,
}: SelfHealingSuspenseProps) {
  const [key, setKey] = useState(0);

  // We keep the remount counter and "committed instance id" in refs so
  // that updating them doesn't trigger a re-render.  Only the watchdog's
  // `setKey` call should cause a remount.
  const remountCountRef = useRef(0);
  const currentInstanceRef = useRef(0);
  const committedInstanceRef = useRef<number>(-1);

  useEffect(() => {
    currentInstanceRef.current = key;

    const exhausted = remountCountRef.current >= maxRemounts;

    if (exhausted && !unboundedRetry) {
      // Exhausted recovery attempts; no further watchdogs.  The current
      // Suspense instance stays as-is so the UI still has a chance to
      // eventually commit through the normal React path.
      return;
    }

    // Once past `maxRemounts`, double the watchdog window each attempt
    // up to 60 s.  `overshoot` of 1 → 2× watchdogMs, 2 → 4×, 3 → 8×, …
    // Pre-`maxRemounts` attempts use the base watchdog as before.
    const overshoot = exhausted
      ? remountCountRef.current - maxRemounts + 1
      : 0;
    const effectiveDelay = exhausted
      ? Math.min(60_000, watchdogMs * 2 ** overshoot)
      : watchdogMs;

    const timer = setTimeout(() => {
      if (committedInstanceRef.current !== currentInstanceRef.current) {
        remountCountRef.current += 1;
        console.warn(
          `[SelfHealingSuspense:${name}] Watchdog fired at ${effectiveDelay}ms ` +
            (exhausted
              ? `(backoff attempt ${remountCountRef.current - maxRemounts}, ` +
                `total ${remountCountRef.current})`
              : `(remount ${remountCountRef.current}/${maxRemounts})`) +
            ` — Suspense boundary never committed for instance ${key}`,
        );
        setKey((k) => k + 1);
      }
    }, effectiveDelay);

    return () => clearTimeout(timer);
  }, [key, name, watchdogMs, maxRemounts, unboundedRetry]);

  const exhaustedForFallback = remountCountRef.current >= maxRemounts;
  const effectiveFallback =
    exhaustedForFallback && errorFallback !== undefined
      ? errorFallback
      : fallback;

  return (
    <Suspense key={key} fallback={effectiveFallback}>
      <CommitSentinel
        committedInstanceRef={committedInstanceRef}
        instanceId={key}
      />
      {children}
    </Suspense>
  );
}

interface CommitSentinelProps {
  committedInstanceRef: MutableRefObject<number>;
  instanceId: number;
}

/**
 * Renders nothing but signals to the parent that the Suspense boundary
 * has committed the resolved subtree.  Placed as the first child inside
 * <Suspense/>, it only mounts once the boundary exits the fallback
 * state; its useEffect then writes the current instance id to the
 * shared ref.  The parent's watchdog timer uses this to decide whether
 * to bump the outer key.
 *
 * Uses a ref (not state) because we explicitly do NOT want to trigger
 * a re-render of the parent — the whole point of the watchdog is to
 * operate below React's render loop.
 */
function CommitSentinel({
  committedInstanceRef,
  instanceId,
}: CommitSentinelProps) {
  useEffect(() => {
    committedInstanceRef.current = instanceId;
  }, [instanceId, committedInstanceRef]);
  return null;
}
